/** 华奋达子应用：SDK 就绪闸门，避免 layout/getKeyData 与 connect 并发导致 0x33 超时 */

import { isHuafendaRhythmQuitHidExclusive } from "./huafendaRhythmQuitHidGate";

export const HFD_SUBAPP_SDK_READY_EVENT = "hfd-subapp-sdk-ready";

declare global {
  interface Window {
    __hfdSubAppSdkReady?: boolean;
    __hfdSubAppSdkReadyPromise?: Promise<boolean>;
  }
}

export function markHuafendaSubAppSdkConnectStarting(): void {
  if (typeof window === "undefined") return;
  window.__hfdSubAppSdkReady = false;
}

/** 等待子应用 index 完成 setDevicesHuafenda（layout / 改键 / 灯光须在此之后调 SDK） */
export function markHuafendaSubAppSdkReady(): void {
  if (typeof window === "undefined") return;
  window.__hfdSubAppSdkReady = true;
  try {
    window.dispatchEvent(new CustomEvent(HFD_SUBAPP_SDK_READY_EVENT));
  } catch {
    // ignore
  }
}

export function isHuafendaSubAppSdkReady(): boolean {
  return typeof window !== "undefined" && window.__hfdSubAppSdkReady === true;
}

function readHfdServiceInstance(): { isConnected?: () => boolean } | null {
  if (typeof window === "undefined") return null;
  try {
    const w = window as {
      sdkApi?: {
        keyboard?: {
          sdkKeyboardBasicApi?: {
            hfdServiceKeyboard?: { instance?: { isConnected?: () => boolean } };
          };
        };
      };
    };
    return (
      w.sdkApi?.keyboard?.sdkKeyboardBasicApi?.hfdServiceKeyboard?.instance ??
      null
    );
  } catch {
    return null;
  }
}

/**
 * 首页已 connect、子应用 init 尚未 mark ready 时快速放行（网页端常见）。
 * 子应用 setDevicesHuafenda 进行中（__hfdSubAppSdkReadyPromise）或律动退出独占期间不放行。
 */
export function tryMarkHuafendaSubAppSdkReadyIfSessionConnected(): boolean {
  if (typeof window === "undefined") return false;
  if (window.__hfdSubAppSdkReady) return true;
  if (isHuafendaRhythmQuitHidExclusive()) return false;
  if (window.__hfdSubAppSdkReadyPromise) return false;

  const inst = readHfdServiceInstance();
  if (typeof inst?.isConnected !== "function" || !inst.isConnected()) {
    return false;
  }
  markHuafendaSubAppSdkReady();
  return true;
}

export function waitForHuafendaSubAppSdkReady(
  timeoutMs = 12000
): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.__hfdSubAppSdkReady) return Promise.resolve(true);
  if (tryMarkHuafendaSubAppSdkReadyIfSessionConnected()) {
    return Promise.resolve(true);
  }

  const pending = window.__hfdSubAppSdkReadyPromise;
  if (pending) {
    return Promise.race([
      pending.then((ok) => {
        // connect 已结束但未 mark 时，按会话连接态补 mark（事件驱动灯光页刷新）
        if (ok && !window.__hfdSubAppSdkReady) {
          tryMarkHuafendaSubAppSdkReadyIfSessionConnected();
        }
        return window.__hfdSubAppSdkReady === true || ok;
      }),
      new Promise<boolean>((resolve) =>
        window.setTimeout(() => {
          if (tryMarkHuafendaSubAppSdkReadyIfSessionConnected()) {
            resolve(true);
            return;
          }
          resolve(window.__hfdSubAppSdkReady === true);
        }, timeoutMs)
      ),
    ]);
  }

  return new Promise<boolean>((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener(HFD_SUBAPP_SDK_READY_EVENT, onReady);
      resolve(ok);
    };
    const onReady = () => finish(true);
    const timer = window.setTimeout(() => {
      if (tryMarkHuafendaSubAppSdkReadyIfSessionConnected()) {
        finish(true);
        return;
      }
      finish(window.__hfdSubAppSdkReady === true);
    }, timeoutMs);
    window.addEventListener(HFD_SUBAPP_SDK_READY_EVENT, onReady);
  });
}

let connectInFlight: Promise<void> | null = null;

/** 等待 setDevicesHuafenda 串行队列空闲（reconnect 期间勿调 getKeyData） */
export function waitForHuafendaConnectIdle(): Promise<void> {
  return connectInFlight ?? Promise.resolve();
}

/** 串行化 setDevicesHuafenda，避免子应用多处同时 reconnect */
export function runHuafendaConnectSerialized(
  fn: () => Promise<void>
): Promise<void> {
  const next = (connectInFlight ?? Promise.resolve()).then(fn, fn);
  connectInFlight = next.catch(() => undefined);
  return next;
}

export function resetHuafendaSubAppSdkGate(): void {
  if (typeof window === "undefined") return;
  window.__hfdSubAppSdkReady = false;
  window.__hfdSubAppSdkReadyPromise = undefined;
  connectInFlight = null;
}
